import { useFeatureFlagsResolved, useFeatureVariant } from '@/services/featureFlags'

/**
 * The server-side flag that turns the whole DUPR+ benefits program on for a user.
 *
 * Authored in the admin UI under Site Management → Feature Flags V2. Its criteria decide the
 * audience (staff email domains first, then a widening rollout); this app only asks which variant
 * came back.
 */
export const BENEFITS_FLAG_KEY = 'enable-benefits-feature'

/**
 * The one variant that means on. Every flag carries a mandatory `control` first variant — the
 * server rejects one that does not — so `control` is the off state and this is the opt-in.
 */
export const BENEFITS_ENABLED_VARIANT = 'enable'

/**
 * Whether this user gets benefits at all: the member's own claim flow AND the coach/club side of
 * answering those claims.
 *
 * One gate for both halves on purpose. They are two ends of the same transaction — a member claims,
 * a coach or a director settles — and a build where only one end existed would produce claims
 * nobody could redeem, or an inbox that could never fill.
 *
 * Unlike [useCanUseCoaching] and [useCanUseClubCapabilities], there is no `isInternalDuprEmail`
 * escape hatch here. Those hooks hard-code the staff domains client-side because their Braze flag
 * cannot express "internal only"; this flag can — `user.email ENDS_WITH "@dupr.com"` is an ordinary
 * criterion — so the audience is authored once, on the server, where it can be widened without an
 * app release.
 *
 * **Off while unknown.** This reads false when signed out, while the flags request is in flight,
 * and when the user simply is not in the audience. All three collapse to the same answer because
 * the alternative is showing an unreleased feature to everyone for the first moments of every page
 * load. Use [useCanUseBenefitsResolved] where the difference matters.
 *
 * The server is the real gate regardless: it will not grant a benefit, list a program, or accept
 * a claim for anybody the underlying entitlements do not cover. This hook decides what to *show*.
 */
export const useCanUseBenefits = (): boolean => useFeatureVariant(BENEFITS_FLAG_KEY, BENEFITS_ENABLED_VARIANT)

/**
 * The same answer, plus whether it is settled yet.
 *
 * Only routes need this. An entry point renders nothing until it knows and nothing when the answer
 * is no, so it cannot tell the two apart and does not need to. A route the user navigated to
 * directly has to: "still loading" means wait, "off" means redirect — and treating the first as the
 * second would bounce a member out of a bookmarked claim link before their flags arrived.
 */
export const useCanUseBenefitsResolved = (): { enabled: boolean; isResolved: boolean } => ({
  enabled: useCanUseBenefits(),
  isResolved: useFeatureFlagsResolved(),
})
